/**
 * Route guards for the billing surfaces.
 *
 * The usage page exists only for a charged tenant (session `billing_visible`).
 * The surfaces that sell credit through Stripe (the top-up page and the
 * billing page) also need the session's `purchases_enabled`: the server's
 * runtime CREDIT_PURCHASES_ENABLED and the tenant's own purchase switch, so
 * switching either on needs no UI rebuild. The public sign-up pages need the
 * global switch.
 *
 * Like every route guard this is UX, not security: the server answers 404 on
 * every /api/billing route for a tenant that is not charged
 * (requireChargedTenant) and on every purchase route while purchasing is off
 * (requirePurchasesEnabled).
 */
import { notFound, redirect } from '@tanstack/react-router';
import { getJson } from '../api/apiBase';
import type { AdminUser } from '../types/admin';

/**
 * The usage page and every billing route exist only when the session carries
 * `billing_visible: true` (a charged tenant). For anyone else a direct visit
 * shows the ordinary not-found page, exactly as for a page that does not exist.
 */
export function notFoundUnlessBillingVisible(user: AdminUser | null | undefined): void {
  if (!user?.billing_visible) {
    // TanStack Router's notFound is a thrown value by design.
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw notFound();
  }
}

/**
 * A signed-in user can buy credit only when the session (/me) carries
 * `purchases_enabled: true` — sent for a charged tenant while purchasing is
 * switched on, and absent otherwise. For anyone else the purchase pages do not
 * exist: a direct visit shows the ordinary not-found page.
 */
export function notFoundUnlessPurchasesEnabled(user: AdminUser | null | undefined): void {
  if (!user?.purchases_enabled) {
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw notFound();
  }
}

/**
 * The public sign-up pages have no session to read the flag from. The list of
 * opening credit amounts answers only while purchasing is switched on and
 * self-service signup is enabled on the server, so it is the signal: a 404 or
 * an unreachable API means the pages do not exist.
 */
export async function signupPurchasesOpen(): Promise<boolean> {
  try {
    await getJson<unknown>('/signup/opening-credit');
    return true;
  } catch {
    return false;
  }
}

/**
 * Guard for the public sign-up pages. With the build-time self-service signup
 * flag off they redirect to the login page; with it on they exist only while
 * the opening-credit list answers, and otherwise show the ordinary not-found
 * page.
 */
export async function guardPublicPurchasePages(selfServiceSignupEnabled: boolean): Promise<void> {
  if (!selfServiceSignupEnabled) {
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({ to: '/admin/login' });
  }
  if (!(await signupPurchasesOpen())) {
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw notFound();
  }
}
